import {
  Box,
  Button,
  FormControlLabel,
  IconButton,
  Popover,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { Settings } from '../../../contexts/SettingsContext'
import useSettings from '../../../hooks/useSettings'
import AdjustmentsIcon from '../../../icons/Adjustments'

const getValues = (settings: Settings) => ({
  compact: settings.compact,
  direction: settings.direction,
  responsiveFontSizes: settings.responsiveFontSizes,
  roundedCorners: settings.roundedCorners,
  theme: settings.theme,
})

const HeaderSettingsButton = () => {
  const { t } = useTranslation()
  const anchorRef = useRef<HTMLButtonElement | null>(null)
  const { settings, saveSettings } = useSettings()
  const [open, setOpen] = useState<boolean>(false)
  const [values, setValues] = useState(getValues(settings))

  useEffect(() => {
    setValues(getValues(settings))
  }, [settings])

  const handleOpen = (): void => {
    setOpen(true)
  }

  const handleClose = (): void => {
    setOpen(false)
  }

  const handleChange = (field: string, value: any): void => {
    setValues({
      ...values,
      [field]: value,
    })
  }

  const handleSave = (): void => {
    saveSettings(values)
    setOpen(false)
  }

  return (
    <>
      <Tooltip title={t('common.settings')}>
        <IconButton
          color='inherit'
          onClick={handleOpen}
          ref={anchorRef}
          size='small'
        >
          <AdjustmentsIcon fontSize='small' />
        </IconButton>
      </Tooltip>
      <Popover
        anchorEl={anchorRef.current}
        anchorOrigin={{
          horizontal: 'right',
          vertical: 'bottom',
        }}
        onClose={handleClose}
        open={open}
        PaperProps={{
          sx: {
            p: 2,
            width: 320,
          },
        }}
      >
        <Typography color='textPrimary' variant='h6'>
          {t('common.settings')}
        </Typography>
        <Box
          sx={{
            mt: 2,
            px: 1.5,
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={values.responsiveFontSizes}
                color='primary'
                edge='start'
                name='responsiveFontSizes'
                onChange={(event): void =>
                  handleChange('responsiveFontSizes', event.target.checked)
                }
              />
            }
            label={t('common.responsiveFontSizes')}
          />
        </Box>
        <Box
          sx={{
            mt: 2,
            px: 1.5,
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={values.compact}
                color='primary'
                edge='start'
                name='compact'
                onChange={(event): void =>
                  handleChange('compact', event.target.checked)
                }
              />
            }
            label={t('common.compact')}
          />
        </Box>
        <Box
          sx={{
            mt: 2,
            px: 1.5,
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={values.roundedCorners}
                color='primary'
                edge='start'
                name='roundedCorners'
                onChange={(event): void =>
                  handleChange('roundedCorners', event.target.checked)
                }
              />
            }
            label={t('common.roundedCorners')}
          />
        </Box>
        <Box sx={{ mt: 3 }}>
          <Button
            color='primary'
            fullWidth
            onClick={handleSave}
            variant='contained'
          >
            {t('common.saveSettings')}
          </Button>
        </Box>
      </Popover>
    </>
  )
}

export default HeaderSettingsButton
